import { Marked } from 'marked';
// DOMPurify directly, not config's sanitizeHtml: config imports the app's stores, and the Help app
// (src/help) renders with this module without them.
import DOMPurify from 'dompurify';

// An instance of its own, so these options never leak into another caller of marked.
const markdown = new Marked({ gfm: true, breaks: true });
// Documents (help articles) are hard-wrapped in their source; there a single newline is a space.
const documentMarkdown = new Marked({ gfm: true, breaks: false });

/**
 * Markdown (an AI reply, for example) as sanitized HTML, safe for v-html. Links open in a new tab;
 * with `{ links: false }` every link is replaced by its text, so nothing in it is clickable (#6453).
 *
 * DOMPurify drops `target` by default, so the link attributes are added to the already sanitized
 * markup rather than asked of the sanitizer: nothing the markdown says survives unsanitized.
 *
 * @param {string|null|undefined} text
 * @param {{ links?: boolean }} [options]
 * @returns {string}
 */
export function renderMarkdown(text, { links, breaks = true } = {}) {
    if (!text) return '';

    const clean = DOMPurify.sanitize((breaks ? markdown : documentMarkdown).parse(String(text), { async: false }));
    if (!clean) return '';

    const template = document.createElement('template');
    template.innerHTML = clean;
    // A paragraph that is nothing but bold text ("**To record a dose:**") is a lead-in heading, not
    // an on-screen label: mark it so styles can tell the two apart.
    template.content.querySelectorAll('p').forEach((paragraph) => {
        const meaningful = [...paragraph.childNodes].filter((node) => !(node.nodeType === Node.TEXT_NODE && !node.textContent.trim()));
        if (meaningful.length === 1 && meaningful[0].nodeName === 'STRONG') paragraph.classList.add('md-lead-in');
    });
    if (links === false) {
        template.content.querySelectorAll('a').forEach((link) => link.replaceWith(...link.childNodes));
        return template.innerHTML;
    }
    template.content.querySelectorAll('a[href]').forEach((link) => {
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
    });
    return template.innerHTML;
}
